import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Plus, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ApiClientError } from '../../../services/apiClient';
import {
  atualizarEmpresa,
  criarEmpresa,
  listarEmpresas,
  substituirPapeisEmpresa,
} from '../../../services/empresasApi';
import {
  EmpresaListQuery,
  EmpresaOperacional,
  EmpresaPapel,
  EmpresaPayload,
  EmpresaStatus,
} from '../../../types/empresa';
import CompanyEditModal from './CompanyEditModal';
import CompanyFilters from './CompanyFilters';
import CompanyTable from './CompanyTable';

const PAGE_SIZE = 10;

function errorMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.code === 'EMPRESA_CNPJ_DUPLICADO'
      ? 'Já existe uma empresa cadastrada com este CNPJ.'
      : error.message;
  }
  return 'Não foi possível concluir a operação.';
}

const EmpresaManagementPage: React.FC = () => {
  const { accessToken, hasPermission } = useAuth();
  const canManage = hasPermission('empresas.gerir');
  const canViewApproval = hasPermission('empresas.aprovar');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') ?? '';
  const [empresas, setEmpresas] = useState<EmpresaOperacional[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(initialSearch);
  const [status, setStatus] = useState<'' | EmpresaStatus>('');
  const [papel, setPapel] = useState<'' | EmpresaPapel>('');
  const [query, setQuery] = useState<Pick<EmpresaListQuery, 'search' | 'status' | 'papel'>>(initialSearch ? { search: initialSearch } : {});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState<EmpresaOperacional | null>(null);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const loadEmpresas = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await listarEmpresas(accessToken, { page, pageSize: PAGE_SIZE, ...query });
      setEmpresas(response.items);
      setTotal(response.total);
    } catch (loadError: unknown) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, query]);

  useEffect(() => {
    loadEmpresas();
  }, [loadEmpresas]);

  const applyFilters = () => {
    setPage(1);
    setQuery({ search: search.trim() || undefined, status: status || undefined, papel: papel || undefined });
  };

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setPapel('');
    setPage(1);
    setQuery({});
  };

  const openCreate = () => {
    setEditingEmpresa(null);
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (empresa: EmpresaOperacional) => {
    setEditingEmpresa(empresa);
    setError(null);
    setModalOpen(true);
  };

  const handleSave = async (payload: EmpresaPayload) => {
    if (!accessToken || !canManage) return;
    setSaving(true);
    setError(null);
    try {
      if (editingEmpresa) {
        const { papeis = [], ...empresaPayload } = payload;
        await atualizarEmpresa(accessToken, editingEmpresa.id, {
          ...empresaPayload,
          nomeFantasia: empresaPayload.nomeFantasia ?? null,
          email: empresaPayload.email ?? null,
          telefone: empresaPayload.telefone ?? null,
          endereco: empresaPayload.endereco ?? null,
          municipio: empresaPayload.municipio ?? null,
          uf: empresaPayload.uf ?? null,
        });
        await substituirPapeisEmpresa(accessToken, editingEmpresa.id, papeis);
        setSuccess('Empresa atualizada com sucesso.');
      } else {
        await criarEmpresa(accessToken, payload);
        setSuccess('Empresa cadastrada com sucesso.');
      }
      setModalOpen(false);
      setEditingEmpresa(null);
      await loadEmpresas();
    } catch (saveError: unknown) {
      setError(errorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (empresa: EmpresaOperacional) => {
    if (!accessToken || !canManage || !window.confirm(`Desativar a empresa ${empresa.razaoSocial}?`)) return;
    setLoading(true);
    setError(null);
    try {
      const { papeis: _papeis, ...payload } = {
        cnpj: empresa.cnpj,
        razaoSocial: empresa.razaoSocial,
        nomeFantasia: empresa.nomeFantasia ?? undefined,
        tipoEmpresa: empresa.tipoEmpresa ?? undefined,
        email: empresa.email ?? undefined,
        telefone: empresa.telefone ?? undefined,
        endereco: empresa.endereco ?? undefined,
        municipio: empresa.municipio ?? undefined,
        uf: empresa.uf ?? undefined,
        status: 'inativo' as const,
        habilitadoGrupoA: empresa.habilitadoGrupoA,
        habilitadoGrupoB: empresa.habilitadoGrupoB,
        habilitadoGrupoE: empresa.habilitadoGrupoE,
        habilitadoAntineoplasico: empresa.habilitadoAntineoplasico,
        tecnologiasTratamentoAutorizadas: empresa.tecnologiasTratamentoAutorizadas,
        ufsAtuacao: empresa.ufsAtuacao,
        papeis: empresa.papeis.map((item) => item.papel),
      };
      await atualizarEmpresa(accessToken, empresa.id, payload);
      setSuccess('Empresa desativada com sucesso.');
      await loadEmpresas();
    } catch (deactivateError: unknown) {
      setError(errorMessage(deactivateError));
    } finally {
      setLoading(false);
    }
  };

  if (!accessToken) {
    return <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900">A gestão real de empresas exige autenticação na API local.</div>;
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3"><div className="rounded-xl bg-emerald-100 p-3 text-emerald-700"><Building2 size={24} /></div><div><h2 className="text-2xl font-bold text-gray-900">Gestão de Empresas</h2><p className="text-sm text-gray-500">Cadastro real de participantes e capacidades operacionais do tenant.</p></div></div>
        <div className="flex gap-2"><button type="button" onClick={loadEmpresas} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"><RefreshCw size={16} /> Atualizar</button>{canManage && <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"><Plus size={16} /> Nova empresa</button>}</div>
      </header>

      {!canManage && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">Seu perfil possui acesso somente para consulta.</div>}
      {error && !modalOpen && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {success && <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{success}</div>}

      <CompanyFilters search={search} status={status} papel={papel} disabled={loading} onSearchChange={setSearch} onStatusChange={setStatus} onPapelChange={setPapel} onSubmit={applyFilters} onClear={clearFilters} />

      <div className="flex items-center justify-between text-sm text-gray-500"><span>{loading ? 'Carregando empresas...' : `${total} empresa(s) encontrada(s)`}</span><span>Página {page} de {totalPages}</span></div>
      {!loading && <CompanyTable empresas={empresas} canManage={canManage} onEdit={openEdit} onDeactivate={handleDeactivate} canViewApproval={canViewApproval} onViewApproval={(empresa) => navigate(`/backoffice/aprovacao-empresas?empresaId=${encodeURIComponent(empresa.id)}`)} />}

      <div className="flex justify-end gap-2"><button type="button" disabled={loading || page <= 1} onClick={() => setPage((current) => current - 1)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm disabled:opacity-40">Anterior</button><button type="button" disabled={loading || page >= totalPages} onClick={() => setPage((current) => current + 1)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm disabled:opacity-40">Próxima</button></div>

      {modalOpen && <CompanyEditModal empresa={editingEmpresa} saving={saving} apiError={error} onSave={handleSave} onCancel={() => { if (!saving) { setModalOpen(false); setEditingEmpresa(null); setError(null); } }} />}
    </div>
  );
};

export default EmpresaManagementPage;
